import { useState, useEffect } from "react";
import { T } from "../../theme";
import { apiCall } from "../../apiClient";
import { Btn, Card, SectionTitle } from "../primitives";

export const DocuSignSettings = () => {
  const [config, setConfig] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [polling, setPolling] = useState(false);

  const load = async () => {
    const d = await apiCall("/esign/config");
    if (d) setConfig(d.config || {});
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const update = (key: string, value: any) => setConfig((p: any) => ({ ...p, [key]: value }));

  const save = async () => {
    setSaving(true); setMsg("");
    const settings: Record<string, any> = {};
    ["docusign_enabled", "docusign_integration_key", "docusign_secret_key", "docusign_account_id", "docusign_base_url", "docusign_auth_url", "docusign_redirect_uri"].forEach(k => {
      if (config[k] !== undefined) settings[k] = config[k];
    });
    // Write-only secrets: GET returns has_* indicators, not the value. Only send
    // when the admin actually typed something, so a blank submit never clobbers a
    // previously-saved key.
    ["docusign_private_key", "docusign_connect_hmac_key"].forEach(k => {
      if (config[k]) settings[k] = config[k];
    });
    const r = await apiCall("/esign/config", { method: "PUT", body: JSON.stringify({ settings }) });
    if (r?.success) { setMsg("Settings saved"); setTimeout(() => setMsg(""), 3000); }
    setSaving(false);
  };

  const connect = async () => {
    const r = await apiCall("/esign/oauth/authorize");
    if (r?.url) window.location.href = r.url;
  };

  const disconnect = async () => {
    if (!window.confirm("Disconnect DocuSign? Active envelopes will continue but new ones cannot be sent.")) return;
    await apiCall("/esign/oauth/disconnect", { method: "POST", body: JSON.stringify({}) });
    load();
  };

  const poll = async () => {
    setPolling(true);
    await apiCall("/esign/poll", { method: "POST", body: JSON.stringify({}) });
    setPolling(false);
    setMsg("Polling completed");
    setTimeout(() => setMsg(""), 3000);
  };

  if (loading) return <div style={{ padding: 20, color: T.txM, fontSize: 13 }}>Loading...</div>;

  return (
    <div>
      <SectionTitle sub="Configure DocuSign e-signature integration for sending contracts">E-Signature (DocuSign)</SectionTitle>
      {msg && <div style={{ padding: "8px 12px", backgroundColor: T.grnS, borderRadius: 5, marginBottom: 12, fontSize: 12, color: T.grn }}>{msg}</div>}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16 }}>
          <span style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: config.connected ? T.grn : T.red, display: "inline-block" }} />
          <span style={{ fontSize: 13, fontWeight: 600, color: T.tx }}>{config.connected ? "Connected to DocuSign" : "Not Connected"}</span>
          <button onClick={() => update("docusign_enabled", config.docusign_enabled === "true" ? "false" : "true")} style={{
            padding: "4px 12px", fontSize: 11, fontWeight: 600, borderRadius: 12, border: "none", cursor: "pointer",
            backgroundColor: config.docusign_enabled === "true" ? T.grn : T.txD, color: "#fff"
          }}>{config.docusign_enabled === "true" ? "Enabled" : "Disabled"}</button>
        </div>
        {config.connected && config.token_expires && (
          <div style={{ fontSize: 11, color: T.txM, marginBottom: 12 }}>Token expires: {new Date(config.token_expires).toLocaleString()}</div>
        )}
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {!config.connected ? (
            <Btn v="primary" sz="sm" onClick={() => { connect(); }}>Connect DocuSign</Btn>
          ) : (
            <Btn v="danger" sz="sm" onClick={() => { disconnect(); }}>Disconnect</Btn>
          )}
          <Btn v="secondary" sz="sm" onClick={() => { poll(); }} disabled={polling}>{polling ? "Polling..." : "Poll Envelopes"}</Btn>
        </div>

        <div style={{ fontSize: 12, fontWeight: 600, color: T.tx, marginBottom: 8 }}>Configuration</div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: T.txD, marginBottom: 4, letterSpacing: ".04em", textTransform: "uppercase" }}>Integration Key (Client ID)</label>
            <input value={config.docusign_integration_key || ""} onChange={e => update("docusign_integration_key", e.target.value)} style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: T.txD, marginBottom: 4, letterSpacing: ".04em", textTransform: "uppercase" }}>Secret Key</label>
            <input type="password" value={config.docusign_secret_key || ""} onChange={e => update("docusign_secret_key", e.target.value)} style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: T.txD, marginBottom: 4, letterSpacing: ".04em", textTransform: "uppercase" }}>Account ID</label>
            <input value={config.docusign_account_id || ""} onChange={e => update("docusign_account_id", e.target.value)} style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: T.txD, marginBottom: 4, letterSpacing: ".04em", textTransform: "uppercase" }}>Base URL</label>
            <input value={config.docusign_base_url || ""} onChange={e => update("docusign_base_url", e.target.value)} style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: T.txD, marginBottom: 4, letterSpacing: ".04em", textTransform: "uppercase" }}>Auth URL</label>
            <input value={config.docusign_auth_url || ""} onChange={e => update("docusign_auth_url", e.target.value)} style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: T.txD, marginBottom: 4, letterSpacing: ".04em", textTransform: "uppercase" }}>Redirect URI (read-only)</label>
            <input value={config.docusign_redirect_uri || ""} readOnly style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg4, color: T.txM, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>
          <div>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: T.txD, marginBottom: 4, letterSpacing: ".04em", textTransform: "uppercase" }}>Connect HMAC Key {config.has_connect_hmac_key && <span style={{ color: T.grn, fontSize: 10 }}>● configured</span>}</label>
            <input type="password" placeholder={config.has_connect_hmac_key ? "•••••• set — leave blank to keep" : ""} value={config.docusign_connect_hmac_key || ""} onChange={e => update("docusign_connect_hmac_key", e.target.value)} style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none", fontFamily: "inherit", boxSizing: "border-box" }} />
          </div>
          <div style={{ gridColumn: "1 / -1" }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: T.txD, marginBottom: 4, letterSpacing: ".04em", textTransform: "uppercase" }}>RSA Private Key — JWT/ISV, optional {config.has_private_key && <span style={{ color: T.grn, fontSize: 10 }}>● configured</span>}</label>
            <textarea value={config.docusign_private_key || ""} onChange={e => update("docusign_private_key", e.target.value)} rows={4} placeholder={config.has_private_key ? "•••••• set — leave blank to keep" : "-----BEGIN RSA PRIVATE KEY-----"} style={{ width: "100%", padding: "8px 10px", borderRadius: 6, border: `1px solid ${T.brd}`, backgroundColor: T.bg3, color: T.tx, fontSize: 12, outline: "none", fontFamily: "monospace", boxSizing: "border-box", resize: "vertical" }} />
          </div>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
          <Btn v="primary" sz="sm" onClick={() => { save(); }} disabled={saving}>{saving ? "Saving..." : "Save Settings"}</Btn>
        </div>
      </Card>
    </div>
  );
};
